import { lazy, useEffect, useState, type ComponentType } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import type { UsuarioSessao } from '@crm/shared'
import { ErroRequisicao } from './api/cliente'
import { ehSessaoEncerrada, useEu } from './api/hooks'
import { Carregando } from './componentes/ui'
import { Layout } from './paginas/Layout'
import { Login } from './paginas/Login'
import { TelaDeEntrada } from './paginas/TelaDeEntrada'
import { BatePapo } from './paginas/bate-papo/BatePapo'
import { EscolhaDeProduto } from './paginas/EscolhaDeProduto'
import {
  abreConfiguracao,
  abreConfiguracaoDaAdministracao,
  rotaInicialDoMarketing,
  pode,
  rotaInicialDaAdministracao,
  rotaInicialDoCrm,
} from './permissao'

/**
 * Página que só baixa quando alguém abre (ADR 0067). Antes o sistema inteiro,
 * com o editor de diagrama e os gráficos, vinha num arquivo de 1,2 MB antes da
 * primeira tela, também para quem abre pelo link público. O bate-papo, que é
 * a tela de quase todo mundo, continua no arquivo principal.
 */
function sobDemanda<P extends object>(carregar: () => Promise<ComponentType<P>>) {
  return Object.assign(
    lazy(() => carregar().then((c) => ({ default: c }))),
    { carregar },
  )
}

const Anuncios = sobDemanda(() => import('./paginas/Anuncios').then((m) => m.Anuncios))
const Calendario = sobDemanda(() => import('./paginas/Calendario').then((m) => m.Calendario))
const CalendarioDePosts = sobDemanda(() =>
  import('./paginas/marketing/CalendarioDePosts').then((m) => m.CalendarioDePosts),
)
const Configuracao = sobDemanda(() =>
  import('./paginas/configuracao/Configuracao').then((m) => m.Configuracao),
)
const Contatos = sobDemanda(() => import('./paginas/contatos/Contatos').then((m) => m.Contatos))
const EditorDePost = sobDemanda(() => import('./paginas/marketing/EditorDePost').then((m) => m.EditorDePost))
const Empresas = sobDemanda(() => import('./paginas/empresas/Empresas').then((m) => m.Empresas))
const Estatisticas = sobDemanda(() => import('./paginas/Estatisticas').then((m) => m.Estatisticas))
const Leads = sobDemanda(() => import('./paginas/leads/Leads').then((m) => m.Leads))
const ListaDePosts = sobDemanda(() => import('./paginas/marketing/ListaDePosts').then((m) => m.ListaDePosts))
const Notificacoes = sobDemanda(() => import('./paginas/Notificacoes').then((m) => m.Notificacoes))
const Painel = sobDemanda(() => import('./paginas/painel/Painel').then((m) => m.Painel))
const PerfisSociais = sobDemanda(() =>
  import('./paginas/marketing/PerfisSociais').then((m) => m.PerfisSociais),
)

/**
 * Depois que o sistema montou, baixa em silêncio as páginas que a pessoa pode
 * abrir: trocar de tela continua instantâneo, e quem só atende não baixa o
 * Painel nem o Marketing.
 */
function useAdiantarPaginas(usuario: UsuarioSessao) {
  useEffect(() => {
    const paginas = [
      Notificacoes,
      ...(pode(usuario, 'calendario.ver') ? [Calendario] : []),
      ...(pode(usuario, 'leads.ver') ? [Leads, Contatos, Empresas] : []),
      ...(pode(usuario, 'anuncios.ver') ? [Anuncios] : []),
      ...(abreConfiguracao(usuario) || abreConfiguracaoDaAdministracao(usuario) ? [Configuracao] : []),
      ...(pode(usuario, 'painel.ver') ? [Painel] : []),
      ...(pode(usuario, 'estatisticas.ver') ? [Estatisticas] : []),
      ...(pode(usuario, 'marketing.ver') ? [CalendarioDePosts, ListaDePosts, EditorDePost, PerfisSociais] : []),
    ]
    // Espera a primeira tela buscar o que precisa; o que é dela vem antes.
    const relogio = window.setTimeout(() => {
      for (const pagina of paginas) void pagina.carregar().catch(() => undefined)
    }, 4000)
    return () => window.clearTimeout(relogio)
  }, [usuario])
}

export function App() {
  const { data: usuario, isPending, error, failureCount } = useEu()
  /*
    A tela de entrada aparece uma vez por carregamento da página: depois do
    login e depois de um F5, que é quando o sistema se monta de novo. Trocar
    de tela dentro do sistema não passa por aqui, então navegar não a traz de
    volta.
  */
  const [mostrarEntrada, setMostrarEntrada] = useState(true)

  const saiu = ehSessaoEncerrada(error)

  /*
    Com a sessão de pé, o sistema continua na tela mesmo que o servidor pare de
    responder por um tempo (numa atualização, por exemplo): o pedido tenta de
    novo sozinho e ninguém perde o que estava fazendo. Só o 401 leva ao login.
  */
  if (!usuario || saiu) {
    // `isPending`, e não `isLoading`: com a janela fora de foco, a nova
    // tentativa fica em pausa, e em pausa "carregando" é falso. Com ele, a
    // tela caía no login por um instante antes de entrar.
    if (!saiu && (isPending || error)) {
      return (
        <Carregando
          texto={
            failureCount > 0 ? 'O servidor não respondeu. Tentando de novo…' : 'Abrindo o sistema…'
          }
        />
      )
    }
    return (
      <Login
        // A sessão caiu pelo horário de trabalho, pelo pedido ou pela contagem (10/10/2026).
        saiuPeloHorario={error instanceof ErroRequisicao && error.corpo.erro === 'fora_do_horario'}
      />
    )
  }

  return (
    <>
      {mostrarEntrada ? (
        <TelaDeEntrada usuario={usuario} aoFechar={() => setMostrarEntrada(false)} />
      ) : null}
      <Rotas usuario={usuario} />
    </>
  )
}

/**
 * As rotas, separadas do `App` de propósito.
 *
 * Elas montam **atrás** da tela de entrada, e é isso que faz a tela valer a
 * pena: a lista de conversas, os contadores e as fotos já estão sendo
 * buscados enquanto a pessoa lê os cartões. Se só montassem depois, os 5
 * segundos seriam 15 segundos a mais de espera, e não a espera de sempre
 * coberta.
 */
function Rotas({ usuario }: { usuario: UsuarioSessao }) {
  useAdiantarPaginas(usuario)
  return (
    <Routes>
      <Route element={<Layout usuario={usuario} />}>
        <Route
          path="/bate-papo"
          element={
            pode(usuario, 'bate_papo.ver') ? (
              <BatePapo usuario={usuario} />
            ) : (
              <SemAcesso />
            )
          }
        />
        {/*
          Chatbot virou aba da Configuração. O endereço antigo continua de pé e
          leva para a aba certa: favorito e link colado no WhatsApp não podem
          virar tela de erro por causa de uma arrumação.
        */}
        <Route path="/chatbot" element={<Navigate to="/configuracao?aba=chatbot" replace />} />
        {/* Estatísticas e Painel do admin moram na Administração (ADR 0059). */}
        <Route path="/estatisticas" element={<Levar para="/admin/estatisticas" />} />
        <Route
          path="/calendario"
          element={
            pode(usuario, 'calendario.ver') ? <Calendario usuario={usuario} /> : <SemAcesso />
          }
        />
        <Route
          path="/leads"
          element={pode(usuario, 'leads.ver') ? <Leads usuario={usuario} /> : <SemAcesso />}
        />
        <Route
          path="/contatos"
          element={pode(usuario, 'leads.ver') ? <Contatos usuario={usuario} /> : <SemAcesso />}
        />
        <Route
          path="/empresas"
          element={pode(usuario, 'leads.ver') ? <Empresas usuario={usuario} /> : <SemAcesso />}
        />
        {/*
          Listas e Logs viraram abas da Configuração.

          Os endereços antigos continuam de pé e redirecionam: notificação,
          link colado no WhatsApp e favorito de quem já usava não podem virar
          tela de erro por causa de uma arrumação de menu.
        */}
        <Route path="/listas" element={<Navigate to="/configuracao?aba=planilhas" replace />} />
        <Route
          path="/configuracao"
          element={abreConfiguracao(usuario) ? <Configuracao usuario={usuario} /> : <SemAcesso />}
        />
        <Route path="/logs" element={<Navigate to="/configuracao?aba=logs" replace />} />
        {/*
          Sem `painel.ver` a rota nem existe para a pessoa: cai no
          "*" e volta para a tela inicial dela. O menu também não mostra o
          item — tela bloqueada à vista só ensina que existe algo escondido.
        */}
        <Route path="/painel" element={<Levar para="/admin/painel" />} />
        {/* Anúncios mudou do CRM para o Marketing (ADR 0068); o endereço antigo leva ao novo. */}
        <Route path="/anuncios" element={<Levar para="/marketing/anuncios" />} />
        {/*
          Sem chave de permissão: notificação é sempre a do próprio usuário, e
          o servidor recorta por `usuarioId`. Não existe "ver as de outro" para
          proteger, e exigir permissão esconderia o aviso de quem precisa dele.
          Fora da barra de navegação de propósito — chega-se pelo sino.
        */}
        <Route path="/notificacoes" element={<Notificacoes />} />
        <Route path="/ajuda" element={<Navigate to="/inicio" replace />} />
        {/* FaroEscolar Administração (ADR 0059). */}
        <Route
          path="/admin"
          element={<Navigate to={rotaInicialDaAdministracao(usuario) ?? '/inicio'} replace />}
        />
        <Route
          path="/admin/painel"
          element={pode(usuario, 'painel.ver') ? <Painel usuario={usuario} /> : <SemAcesso />}
        />
        <Route
          path="/admin/estatisticas"
          element={
            pode(usuario, 'estatisticas.ver') ? <Estatisticas usuario={usuario} /> : <SemAcesso />
          }
        />
        <Route
          path="/admin/configuracao"
          element={
            abreConfiguracaoDaAdministracao(usuario) ? (
              <Configuracao usuario={usuario} area="administracao" />
            ) : (
              <SemAcesso />
            )
          }
        />
        {/* A logo leva aqui: a escolha entre o CRM, o Marketing e a Administração (ADR 0057 e 0059). */}
        <Route path="/inicio" element={<EscolhaDeProduto usuario={usuario} />} />
        <Route
          path="/marketing"
          element={
            pode(usuario, 'marketing.ver') ? (
              <CalendarioDePosts usuario={usuario} />
            ) : (
              <Navigate to={rotaInicialDoMarketing(usuario) ?? '/inicio'} replace />
            )
          }
        />
        <Route
          path="/marketing/posts"
          element={pode(usuario, 'marketing.ver') ? <ListaDePosts usuario={usuario} /> : <SemAcesso />}
        />
        <Route
          path="/marketing/posts/novo"
          element={
            pode(usuario, 'marketing.criar') ? <EditorDePost usuario={usuario} /> : <SemAcesso />
          }
        />
        <Route
          path="/marketing/posts/:id"
          element={pode(usuario, 'marketing.ver') ? <EditorDePost usuario={usuario} /> : <SemAcesso />}
        />
        <Route
          path="/marketing/perfis"
          element={pode(usuario, 'marketing.ver') ? <PerfisSociais usuario={usuario} /> : <SemAcesso />}
        />
        <Route
          path="/marketing/anuncios"
          element={pode(usuario, 'anuncios.ver') ? <Anuncios usuario={usuario} /> : <SemAcesso />}
        />
        <Route path="*" element={<Navigate to={rotaInicial(usuario)} replace />} />
      </Route>
    </Routes>
  )
}

function rotaInicial(usuario: UsuarioSessao): string {
  const doCrm = rotaInicialDoCrm(usuario)
  if (doCrm) return doCrm
  // Quem usa só o Marketing (ADR 0057) ou só a Administração (ADR 0059) cai direto nele.
  const doMarketing = rotaInicialDoMarketing(usuario)
  if (doMarketing) return doMarketing
  const daAdministracao = rotaInicialDaAdministracao(usuario)
  if (daAdministracao) return daAdministracao
  // Sem tela nenhuma: a página de escolha, que diz que o usuário não abre nada ainda.
  return '/inicio'
}

/** Endereço antigo que mudou de casa: leva junto o que vinha depois do "?". */
function Levar({ para }: { para: string }) {
  const { search } = useLocation()
  return <Navigate to={`${para}${search}`} replace />
}

function SemAcesso() {
  return (
    <div className="rounded-2xl bg-superficie p-6 shadow-suave ring-1 ring-tinta-200/60 text-sm text-tinta-600">
      Seu usuário não tem permissão para esta tela. Fale com o administrador.
    </div>
  )
}
